export const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/avif",
]);

export const ALLOWED_IMAGE_FORMATS = ["PNG", "JPG/JPEG", "WEBP", "AVIF"] as const;
export const ALLOWED_IMAGE_FORMATS_LABEL = ALLOWED_IMAGE_FORMATS.join(", ");

export const IMAGE_INPUT_ACCEPT_ATTR = "image/png,image/jpeg,image/webp,image/avif";
export const MAX_IMAGE_BYTES_DEFAULT = 5 * 1024 * 1024;
export const MAX_AVATAR_IMAGE_BYTES = 2 * 1024 * 1024;

export interface ValidateImageOptions {
  maxBytes?: number;
  allowedMimeTypes?: Set<string>;
}

export interface ValidateImageResult {
  ok: boolean;
  error?: string;
}

const MB_BYTES = 1024 * 1024;

export function formatMaxSizeLabel(bytes: number): string {
  const mb = bytes / MB_BYTES;
  return Number.isInteger(mb) ? `${mb}MB` : `${mb.toFixed(1)}MB`;
}

function getAllowedExtLabel(allowedMimeTypes: Set<string>): string {
  if (
    allowedMimeTypes.has("image/png") &&
    allowedMimeTypes.has("image/jpeg") &&
    allowedMimeTypes.has("image/webp") &&
    allowedMimeTypes.has("image/avif")
  ) {
    return ALLOWED_IMAGE_FORMATS_LABEL;
  }
  return "supported image";
}

export function isAllowedImageMime(
  file: Pick<File, "type">,
  allowedMimeTypes: Set<string> = ALLOWED_IMAGE_MIME_TYPES,
): boolean {
  const mime = String(file?.type || "").toLowerCase();
  return allowedMimeTypes.has(mime);
}

export function validateImageFile(
  file: Pick<File, "name" | "type" | "size">,
  options: ValidateImageOptions = {},
): ValidateImageResult {
  const maxBytes = options.maxBytes ?? MAX_IMAGE_BYTES_DEFAULT;
  const allowedMimeTypes = options.allowedMimeTypes ?? ALLOWED_IMAGE_MIME_TYPES;

  if (!isAllowedImageMime(file, allowedMimeTypes)) {
    return {
      ok: false,
      error: `Please upload a ${getAllowedExtLabel(allowedMimeTypes)} image.`,
    };
  }

  if (Number(file?.size || 0) > maxBytes) {
    return {
      ok: false,
      error: `Image must be ${formatMaxSizeLabel(maxBytes)} or less.`,
    };
  }

  return { ok: true };
}

export function validateImageFiles(
  files: Array<Pick<File, "name" | "type" | "size">>,
  options: ValidateImageOptions = {},
): { validFiles: Array<Pick<File, "name" | "type" | "size">>; errors: string[] } {
  const validFiles: Array<Pick<File, "name" | "type" | "size">> = [];
  const errors: string[] = [];

  for (const file of files) {
    const result = validateImageFile(file, options);
    if (result.ok) {
      validFiles.push(file);
      continue;
    }

    if (result.error) {
      errors.push(file?.name ? `${file.name}: ${result.error}` : result.error);
    }
  }

  return { validFiles, errors };
}
